import React, { useState, useEffect, useRef } from 'react';
import { Chip, CircularProgress } from '@mui/material';
import api from '../../services/api';
import { useChat } from '../../contexts/ChatContext';

/**
 * AstrologerStatusToggle — lets astrologers toggle online/offline.
 * Shows current status as a coloured chip. Clicking it cycles the status.
 * Fetches initial status from backend and listens for socket updates.
 */
const AstrologerStatusToggle = () => {
  const [status, setStatus] = useState('offline');
  const [loading, setLoading] = useState(true);
  const { socket } = useChat();
  const debounceRef = useRef(null);

  // Fetch current status on mount (once only, ignore rate-limit errors silently)
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchStatus = async () => {
      try {
        const resp = await api.get('/astrologer/status');
        setStatus(resp.data?.status || 'offline');
      } catch (err) {
        // Silently ignore 429 — component will retry via socket events
        if (err?.response?.status !== 429) {
          console.error('Failed to fetch status', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  // Listen for status changes from socket (e.g. after call ends, backend sets back to "online")
  useEffect(() => {
    if (!socket) return;
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    const handler = ({ astrologerId, status: newStatus }) => {
      if (astrologerId && newStatus) {
        // Debounce — at most one fetch per 3 seconds to avoid rate limiting
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          api.get('/astrologer/status').then(resp => {
            setStatus(resp.data?.status || newStatus);
          }).catch(() => {
            setStatus(newStatus);
          });
        }, 3000);
      }
    };

    const consultationEndedHandler = () => {
      // After consultation ends, backend resets status to "online"
      setStatus('online');
    };

    socket.on('astrologer-status-change', handler);
    socket.on('consultation_ended', consultationEndedHandler);
    return () => {
      socket.off('astrologer-status-change', handler);
      socket.off('consultation_ended', consultationEndedHandler);
    };
  }, [socket]);

  const statusColors = {
    online: 'success',
    offline: 'default',
    busy: 'warning',
  };

  const statusLabels = {
    online: 'Online',
    offline: 'Offline',
    busy: 'Busy',
  };

  const handleToggle = async () => {
    if (status === 'busy') return; // Can't toggle while in a call
    const next = status === 'online' ? 'offline' : 'online';
    setLoading(true);
    try {
      await api.put('/astrologer/status', { status: next });
      setStatus(next);
    } catch (err) {
      console.error('Failed to update status', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CircularProgress size={20} />;

  return (
    <Chip
      label={statusLabels[status] || status}
      color={statusColors[status] || 'default'}
      onClick={handleToggle}
      clickable={status !== 'busy'}
      size="small"
      style={{ cursor: status === 'busy' ? 'default' : 'pointer', fontWeight: 600 }}
    />
  );
};

export default AstrologerStatusToggle;
